// src/utils/errorHandler.js
import toast from 'react-hot-toast';

/**
 * Centralized error handling utility for API errors
 * @param {Error} error - The error object from API call
 * @param {string} defaultMessage - Default message to show if no specific error is found
 * @param {boolean} showToast - Whether to show toast notification (default: true)
 * @returns {string} - The error message
 */
export const handleApiError = (error, defaultMessage = 'An error occurred. Please try again.', showToast = true) => {
  let errorMessage = defaultMessage;

  // Handle different types of errors
  if (error.response) {
    const { status, data } = error.response;
    
    // Handle specific status codes
    switch (status) {
      case 400:
        errorMessage = data?.detail || data?.message || 'Bad request. Please check your input.';
        break;
      case 401:
        errorMessage = 'Please log in to continue.';
        break;
      case 403:
        errorMessage = data?.detail || 'You do not have permission to perform this action.';
        break;
      case 404:
        errorMessage = 'The requested resource was not found.';
        break;
      case 429:
        errorMessage = 'Too many requests. Please wait and try again.';
        break;
      case 500:
      case 502:
      case 503:
        errorMessage = 'Server error. Please try again later.';
        break;
      default:
        // Use error message from response if available
        errorMessage = data?.detail || data?.message || data?.error || defaultMessage;
    }
  } else if (error.request) {
    // Network error
    errorMessage = 'Network error. Please check your connection and try again.';
  } else if (error.message) {
    // Other errors
    errorMessage = error.message;
  }

  // Show toast notification if enabled
  if (showToast) {
    toast.error(errorMessage);
  }

  return errorMessage;
};

/**
 * Show success toast notification
 * @param {string} message - Success message to display
 */
export const showSuccess = (message) => {
  toast.success(message);
};

/**
 * Show info toast notification
 * @param {string} message - Info message to display
 */
export const showInfo = (message) => {
  toast(message, {
    icon: 'ℹ️',
  });
};

/**
 * Show warning toast notification
 * @param {string} message - Warning message to display
 */
export const showWarning = (message) => {
  toast(message, {
    icon: '⚠️',
    duration: 4000,
  });
};

/**
 * Custom toast for loading states
 * @param {Promise} promise - Promise to handle
 * @param {Object} messages - Object with loading, success, and error messages
 * @returns {Promise} - The original promise
 */
export const toastPromise = (promise, messages) => {
  return toast.promise(promise, {
    loading: messages.loading || 'Loading...',
    success: messages.success || 'Success!',
    error: (err) => handleApiError(err, messages.error || 'Operation failed', false),
  });
};
